import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import initialInspectionReportsService from '../services/initialInspectionReportsService';
import s3Service from '../services/s3Service';
import PhotoGallery from '../components/PhotoGallery';
import styles from './InitialInspectionCreate.module.css';
import {
  saveDraft,
  deleteDraft,
  getResumableDraftByReport,
  OFFLINE_PHOTO_PREFIX,
} from '../lib/offlineDb';
import { triggerSync } from '../lib/inspectionSyncEngine';

const INITIAL_INSPECTION_IMAGE_FIELDS = [
  { field: 'image_gas_meters', label: 'Gas Meters' },
  { field: 'image_electric_meters', label: 'Electric Meters' },
  { field: 'image_water_meters', label: 'Water Meters' },
  { field: 'image_fuse_boards', label: 'Fuse Boards' },
];

const InitialInspectionEdit = () => {
  const { propertyId, reportId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [property, setProperty] = useState(null);
  const [report, setReport] = useState(null);
  const [buildingTypes, setBuildingTypes] = useState([]);
  const [roomSizes, setRoomSizes] = useState({});
  const [roomsNumber, setRoomsNumber] = useState(0);
  const [errors, setErrors] = useState({});
  const [fileNames, setFileNames] = useState({
    image_gas_meters: "No file chosen",
    image_electric_meters: "No file chosen",
    image_water_meters: "No file chosen",
    image_fuse_boards: "No file chosen",
  });

  // Large image viewer state — single gallery, slides built from the 4 image fields
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  // Rotation persistence — same pattern as monthly inspection. Backend rotates
  // the S3 file in place on submit, then we reset.
  const [photoRotations, setPhotoRotations] = useState({});
  const rotatePhotoField = (field) => {
    setPhotoRotations((prev) => ({
      ...prev,
      [field]: ((prev[field] || 0) + 90) % 360,
    }));
  };

  // Local data-URL previews for freshly uploaded files (before save). Lets the
  // lightbox show the new image immediately without waiting on S3.
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState({});

  // Refs for date picker inputs
  const reportDateInputRef = useRef(null);
  const issuedDateInputRef = useRef(null);
  const reviewDateInputRef = useRef(null);

  // Helper function to format date to Laravel format (DD/MM/YYYY)
  const formatDateToLaravel = (dateValue) => {
    if (!dateValue) return '';
    
    // If already in DD/MM/YYYY format, return as is
    if (typeof dateValue === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
      return dateValue;
    }
    
    // Try to parse the date
    let date;
    if (typeof dateValue === 'string') {
      // Handle ISO format (2025-11-24T19:00:00.000) or date-only (2025-11-24)
      if (dateValue.includes('T')) {
        // ISO format with time
        date = new Date(dateValue);
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        // Date-only format (YYYY-MM-DD)
        date = new Date(dateValue + 'T00:00:00');
      } else {
        // Try parsing as-is
        date = new Date(dateValue);
      }
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      return '';
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '';
    }
    
    // Format to DD/MM/YYYY (Laravel format)
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  };

  // Convert DD/MM/YYYY to YYYY-MM-DD for date input
  const convertToDateInputFormat = (dateString) => {
    if (!dateString) return '';
    // If already in YYYY-MM-DD format, return as is
    if (dateString.includes('-') && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // Handle DD/MM/YYYY format (Laravel format)
    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
    return '';
  };

  // Convert YYYY-MM-DD to DD/MM/YYYY (Laravel format)
  const convertFromDateInputFormat = (dateString) => {
    if (!dateString) return '';
    if (dateString.includes('-')) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    return dateString;
  };

  // Handle date change from native date picker
  const handleDateChange = (fieldName, e) => {
    const dateValue = e.target.value;
    if (dateValue) {
      const formattedDate = convertFromDateInputFormat(dateValue);
      setFormData(prev => ({
        ...prev,
        [fieldName]: formattedDate
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  // Open date picker
  const openDatePicker = (fieldName, e) => {
    e?.preventDefault();
    let dateInputRef;
    if (fieldName === 'report_date') {
      dateInputRef = reportDateInputRef;
    } else if (fieldName === 'issued') {
      dateInputRef = issuedDateInputRef;
    } else if (fieldName === 'review_date') {
      dateInputRef = reviewDateInputRef;
    }
    
    if (dateInputRef?.current) {
      setTimeout(() => {
        try {
          if (dateInputRef.current.showPicker) {
            dateInputRef.current.showPicker();
          } else {
            dateInputRef.current.click();
          }
        } catch (error) {
          dateInputRef.current.click();
        }
      }, 0);
    }
  };

  const [formData, setFormData] = useState({
    property_id: propertyId,
    status_id: 0,
    address: '',
    client: '',
    what_council: '',
    building_type: '',
    property_for_voa: 0,
    floors: '',
    toilets: '',
    bathrooms: '',
    kitchens: '',
    parking: 0,
    garden: 0,
    rooms_number: 0,
    rooms: [],
    maintenance: '',
    installation_shower_cubicles: '',
    installation_shower_electric_units: '',
    installation_kitchens: '',
    installation_electric_heaters: '',
    installation_over_sink_water_heaters: '',
    installation_fridges: '',
    installation_mini_ovens: '',
    installation_microwaves: '',
    installation_cookers: '',
    locations_of_meters: '',
    image_gas_meters: null,
    image_electric_meters: null,
    image_water_meters: null,
    image_fuse_boards: null,
    utility_bills_client: '',
    utility_bills_ggm: '',
    waste_collection_client: '',
    waste_collection_ggm: '',
    surveyed_by: '',
    signed: '',
    report_date: new Date().toLocaleDateString('en-GB'),
    form: 'Q15',
    issued: new Date().toLocaleDateString('en-GB'),
    review_date: new Date().toLocaleDateString('en-GB'),
    approved: '',
    notes: ''
  });

  // ---------------------------------------------------------------------------
  // Offline upload support (initial inspection — edit mode, PUT by reportId)
  // ---------------------------------------------------------------------------
  const genUuid = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });

  const draftRef = useRef({ id: null, clientUuid: null, photos: {} });
  const draftActiveRef = useRef(false);
  const objectUrlsRef = useRef({});
  const formDataRef = useRef(formData);
  const photoRotationsRef = useRef(photoRotations);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  useEffect(() => {
    photoRotationsRef.current = photoRotations;
  }, [photoRotations]);

  const ensureDraftId = () => {
    if (!draftRef.current.id) {
      draftRef.current.id = `iir-edit-${reportId}`;
      draftRef.current.clientUuid = genUuid();
      if (!draftRef.current.photos) draftRef.current.photos = {};
    }
  };

  const snapshotFormData = () => {
    const fd = {};
    Object.keys(formDataRef.current).forEach((k) => {
      if (!k.endsWith("_url")) fd[k] = formDataRef.current[k];
    });
    const files = draftRef.current.photos || {};
    Object.keys(files).forEach((key) => {
      const rec = files[key];
      if (rec && rec.field) {
        fd[rec.field] = rec.s3Key
          ? rec.s3Key.split("/").pop()
          : OFFLINE_PHOTO_PREFIX + key;
      }
    });
    return fd;
  };

  const persistDraft = async (status) => {
    ensureDraftId();
    await saveDraft({
      id: draftRef.current.id,
      propertyId: String(propertyId),
      reportId: String(reportId),
      reportType: "initial",
      mode: "update",
      clientUuid: draftRef.current.clientUuid,
      status,
      formData: snapshotFormData(),
      photoRotations: photoRotationsRef.current || {},
      photos: draftRef.current.photos || {},
      attempts: 0,
      permanent: false,
      lastError: null,
    });
    draftActiveRef.current = true;
  };

  const handleOfflineSave = async (file, field) => {
    ensureDraftId();
    const prev = draftRef.current.photos[field];
    draftRef.current.photos[field] = {
      blob: file,
      fileName: file.name,
      mimeType: file.type,
      folder: `uploads/images/properties/${propertyId}/reports/iir/`,
      field,
      filenameOnly: true,
    };
    try {
      await persistDraft("draft");
    } catch (err) {
      if (prev) draftRef.current.photos[field] = prev;
      else delete draftRef.current.photos[field];
      throw err;
    }
    return OFFLINE_PHOTO_PREFIX + field;
  };

  const queueForSync = async () => {
    await persistDraft("queued");
    toast.info(
      "Saved. Your changes will upload automatically when you’re back online."
    );
    triggerSync();
    navigate(`/properties/${propertyId}`);
  };

  const restoreDraft = async () => {
    try {
      const saved = await getResumableDraftByReport(reportId, "initial");
      if (!saved) return;
      draftRef.current = {
        id: saved.id,
        clientUuid: saved.clientUuid,
        photos: saved.photos || {},
      };
      draftActiveRef.current = true;
      if (saved.photoRotations) setPhotoRotations(saved.photoRotations);
      const previews = {};
      const names = {};
      Object.values(saved.photos || {}).forEach((rec) => {
        if (rec && rec.blob && rec.field) {
          const url = URL.createObjectURL(rec.blob);
          objectUrlsRef.current[rec.field] = url;
          previews[rec.field] = url;
          names[rec.field] = `${rec.fileName || "photo"} (saved offline)`;
        }
      });
      if (saved.formData) setFormData((prev) => ({ ...prev, ...saved.formData }));
      if (Object.keys(previews).length)
        setPhotoPreviewUrls((p) => ({ ...p, ...previews }));
      if (Object.keys(names).length) setFileNames((p) => ({ ...p, ...names }));
      toast.info("Resumed your saved offline edits.");
    } catch (e) {
      console.warn("[IIR edit] restore draft failed:", e && e.message);
    }
  };

  useEffect(() => {
    if (!draftActiveRef.current) return undefined;
    const t = setTimeout(() => {
      persistDraft("draft").catch(() => {});
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, photoRotations]);

  useEffect(
    () => () => {
      Object.values(objectUrlsRef.current).forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch (_) {
          /* no-op */
        }
      });
    },
    []
  );

  useEffect(() => {
    loadInitialData();
  }, [propertyId, reportId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load property data and configurations
      const propertyResponse = await initialInspectionReportsService.getFormData(propertyId);
      const propertyData = propertyResponse.data.property;
      setProperty(propertyData);
      
      // Set building types and room sizes from form data response
      setBuildingTypes(propertyResponse.data.buildingTypes);
      setRoomSizes(propertyResponse.data.roomSizes);
      setRoomsNumber(propertyResponse.data.roomsNumber);
      
      // Load report data
      const reportResponse = await initialInspectionReportsService.getInitialInspectionReport(reportId);
      const reportData = reportResponse.data;
      setReport(reportData);
      
      console.log('🔍 [FRONTEND] Report data loaded:', reportData);
      console.log('🔍 [FRONTEND] Rooms data type:', typeof reportData.rooms);
      console.log('🔍 [FRONTEND] Rooms data value:', reportData.rooms);
      
      // Set form data with report information
      // Backend already parses rooms from JSON string to array, so use directly
      // Format dates to Laravel format (DD/MM/YYYY)
      // If address is empty in report, use property's fullAddress
      const reportAddress = reportData.address && reportData.address.trim() !== '' 
        ? reportData.address 
        : (propertyData?.fullAddress || '');
      
      setFormData(prev => ({
        ...prev,
        ...reportData,
        address: reportAddress, // Use report address or fallback to property fullAddress
        rooms: reportData.rooms || [],
        report_date: formatDateToLaravel(reportData.report_date),
        issued: formatDateToLaravel(reportData.issued),
        review_date: formatDateToLaravel(reportData.review_date),
      }));
      
      console.log('🔍 [FRONTEND] Address from report:', reportData.address);
      console.log('🔍 [FRONTEND] Address from property:', propertyData?.fullAddress);
      console.log('🔍 [FRONTEND] Final address:', reportAddress);
      
      // Update roomsNumber state based on loaded report data
      if (reportData.rooms_number) {
        setRoomsNumber(reportData.rooms_number);
      }
      
      // Update file names for existing images - extract actual file name from S3 key
      const getFileNameFromS3Key = (s3Key) => {
        if (!s3Key) return "No file chosen";
        // Extract filename from S3 key (e.g., "initial-inspection-reports/1234567890_gas_meters.jpg" -> "gas_meters.jpg")
        const parts = s3Key.split('/');
        const fileName = parts[parts.length - 1];
        // Remove timestamp prefix if present (e.g., "1234567890_gas_meters.jpg" -> "gas_meters.jpg")
        return fileName.replace(/^\d+_/, '');
      };
      
      setFileNames(prev => ({
        ...prev,
        image_gas_meters: getFileNameFromS3Key(reportData.image_gas_meters),
        image_electric_meters: getFileNameFromS3Key(reportData.image_electric_meters),
        image_water_meters: getFileNameFromS3Key(reportData.image_water_meters),
        image_fuse_boards: getFileNameFromS3Key(reportData.image_fuse_boards),
      }));

      // Resume any saved offline edit draft for this report (after load).
      await restoreDraft();

    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === 'file') {
      const file = files[0];
      if (file) {
        // Validate file type (jpg, jpeg, png)
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
          toast.error('Please select a valid image file (JPG, JPEG, or PNG)');
          return;
        }

        // Validate file size (max 100MB as per Laravel)
        const maxSize = 100 * 1024 * 1024; // 100MB
        if (file.size > maxSize) {
          toast.error('File size must be less than 100MB');
          return;
        }
        handleFileUpload(file, name);
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: null,
        }));
        setFileNames((prev) => ({
          ...prev,
          [name]: "No file chosen",
        }));
      }
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: value === '' ? '' : parseInt(value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleFileUpload = async (file, fieldName) => {
    const folder = `uploads/images/properties/${propertyId}/reports/iir/`;

    // Read file locally so the thumbnail/lightbox shows the picked image
    // immediately (works offline too).
    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoPreviewUrls((prev) => ({ ...prev, [fieldName]: e.target.result }));
    };
    reader.readAsDataURL(file);

    const OFFLINE_MAX_SIZE = 25 * 1024 * 1024; // 25MB cap for offline buffering

    const bufferOffline = async () => {
      if (file.size > OFFLINE_MAX_SIZE) {
        toast.error(
          "While offline, images must be under 25MB. Upload larger files when you’re back online."
        );
        setFileNames((prev) => ({ ...prev, [fieldName]: "No file chosen" }));
        setPhotoPreviewUrls((prev) => {
          const n = { ...prev };
          delete n[fieldName];
          return n;
        });
        return;
      }
      setFileNames((prev) => ({ ...prev, [fieldName]: "Saving offline..." }));
      try {
        const placeholder = await handleOfflineSave(file, fieldName);
        setFormData((prev) => ({ ...prev, [fieldName]: placeholder }));
        setFileNames((prev) => ({
          ...prev,
          [fieldName]: `${file.name} (saved offline)`,
        }));
      } catch (err) {
        setFileNames((prev) => ({ ...prev, [fieldName]: "No file chosen" }));
        setPhotoPreviewUrls((prev) => {
          const n = { ...prev };
          delete n[fieldName];
          return n;
        });
        const full =
          /quota/i.test(err?.message || "") || err?.name === "QuotaExceededError";
        toast.error(
          full
            ? "This device is out of storage, so the image could not be saved for offline upload."
            : "Offline storage is unavailable, so the image cannot be saved while offline. Try again once you are back online."
        );
      }
    };

    const isOnline = typeof navigator === "undefined" ? true : navigator.onLine;
    if (!isOnline) {
      await bufferOffline();
      return;
    }

    try {
      setFileNames((prev) => ({ ...prev, [fieldName]: "Uploading..." }));

      // Bound the online upload so a dead zone falls back to offline buffering.
      const s3Key = await new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error("Upload timed out")), 30000);
        s3Service
          .uploadImage(file, {
            maxSize: 100 * 1024 * 1024, // 100MB
            allowedTypes: ['image/jpeg', 'image/jpg', 'image/png'],
            folder: folder,
            useLaravelStructure: true,
            onProgress: (progress) => {
              setFileNames((prev) => ({
                ...prev,
                [fieldName]: `Uploading... ${Math.round(progress)}%`,
              }));
            },
          })
          .then((k) => {
            clearTimeout(timer);
            resolve(k);
          })
          .catch((e) => {
            clearTimeout(timer);
            reject(e);
          });
      });

      const filename = s3Key.split('/').pop();
      setFormData((prev) => ({ ...prev, [fieldName]: filename }));
      setFileNames((prev) => ({ ...prev, [fieldName]: file.name }));
      if (draftRef.current.photos[fieldName]) {
        delete draftRef.current.photos[fieldName];
      }
      toast.success(`${fieldName.replace('image_', '').replace('_', ' ')} uploaded successfully`);
    } catch (error) {
      console.error('File upload error:', error);
      // Network/timeout failure → buffer offline instead of losing the image.
      await bufferOffline();
    }
  };

  const handleRemoveImage = async (fieldName) => {
    try {
      // Show confirmation dialog
      const confirmMessage = `Are you sure you want to remove the ${fieldName.replace('image_', '').replace('_', ' ')} image?`;
      if (!window.confirm(confirmMessage)) {
        return;
      }

      // Call backend API to remove image
      await initialInspectionReportsService.removeImage(reportId, fieldName);

      // Update local state immediately
      setFormData((prev) => ({
        ...prev,
        [fieldName]: "", // Use empty string to match backend
      }));
      setFileNames((prev) => ({
        ...prev,
        [fieldName]: "No file chosen",
      }));

      // Update report state to clear presigned URL
      setReport((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          [fieldName]: "", // Use empty string to match backend
          [`${fieldName}_url`]: null,
        };
      });

      // Reload report data to get fresh state from backend
      const reportResponse = await initialInspectionReportsService.getInitialInspectionReport(reportId);
      const reportData = reportResponse.data;
      setReport(reportData);

      // Update form data with refreshed report (excluding the removed image field)
      // Format dates to Laravel format (DD/MM/YYYY)
      setFormData((prev) => {
        const updated = {
          ...prev,
          ...reportData,
          rooms: reportData.rooms || [],
          report_date: formatDateToLaravel(reportData.report_date),
          issued: formatDateToLaravel(reportData.issued),
          review_date: formatDateToLaravel(reportData.review_date),
        };
        // Ensure removed image field is empty string (backend returns empty string, not null)
        updated[fieldName] = reportData[fieldName] || "";
        return updated;
      });

      // Update file names
      const getFileNameFromS3Key = (s3Key) => {
        if (!s3Key || s3Key === "") return "No file chosen";
        const parts = s3Key.split('/');
        const fileName = parts[parts.length - 1];
        return fileName.replace(/^\d+_/, '');
      };

      setFileNames((prev) => ({
        ...prev,
        [fieldName]: "No file chosen",
        image_gas_meters: getFileNameFromS3Key(reportData.image_gas_meters),
        image_electric_meters: getFileNameFromS3Key(reportData.image_electric_meters),
        image_water_meters: getFileNameFromS3Key(reportData.image_water_meters),
        image_fuse_boards: getFileNameFromS3Key(reportData.image_fuse_boards),
      }));

      toast.success(`${fieldName.replace('image_', '').replace('_', ' ')} removed successfully`);
    } catch (error) {
      console.error('Error removing image:', error);
      toast.error(`Failed to remove ${fieldName.replace('image_', '').replace('_', ' ')}: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDownloadImage = async (fieldName) => {
    try {
      const s3Key = formData[fieldName];
      if (s3Key) {
        // Use S3 service to download file
        const fileName = `${fieldName.replace('image_', '').replace('_', ' ')}.jpg`;
        await s3Service.downloadFile(s3Key, fileName);
        toast.success(`${fieldName.replace('image_', '').replace('_', ' ')} downloaded successfully`);
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error(`Failed to download ${fieldName.replace('image_', '').replace('_', ' ')}`);
    }
  };

  const handleRoomsNumberChange = (e) => {
    const newRoomsNumber = parseInt(e.target.value) || 0;
    setRoomsNumber(newRoomsNumber);
    
    setFormData(prev => ({
      ...prev,
      rooms_number: newRoomsNumber,
      rooms: Array(newRoomsNumber).fill('')
    }));
  };

  const handleRoomChange = (index, value) => {
    const newRooms = [...formData.rooms];
    newRooms[index] = value;
    
    setFormData(prev => ({
      ...prev,
      rooms: newRooms
    }));
  };


  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.status_id && formData.status_id !== 0) {
      newErrors.status_id = 'Status is required';
    }
    
    if (!formData.rooms_number && formData.rooms_number !== 0) {
      newErrors.rooms_number = 'Number of rooms is required';
    }
    
    if (formData.rooms_number > 0) {
      formData.rooms.forEach((room, index) => {
        if (!room && room !== 0) {
          newErrors[`rooms.${index}`] = `Room ${index + 1} is required`;
        }
      });
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    const hasBufferedImages =
      Object.keys(draftRef.current.photos || {}).length > 0;
    const isOnline = typeof navigator === "undefined" ? true : navigator.onLine;

    setLoading(true);

    // Offline, or images still buffered locally: queue the edit for background sync.
    if (!isOnline || hasBufferedImages) {
      try {
        await queueForSync();
      } catch (err) {
        setLoading(false);
        toast.error(
          "Could not save these changes for offline upload: " +
            (err?.message || "storage unavailable")
        );
      }
      return;
    }

    try {
      // Merge pending rotations into the submit payload. Backend rotates the
      // S3 file in place and strips these fields before DB save.
      const submitData = { ...formData };
      Object.entries(photoRotations).forEach(([field, degrees]) => {
        if (degrees) submitData[`${field}_rotate`] = degrees;
      });

      await initialInspectionReportsService.updateInitialInspectionReport(reportId, submitData);
      setPhotoRotations({});
      if (draftActiveRef.current) {
        await deleteDraft(draftRef.current.id).catch(() => {});
        draftActiveRef.current = false;
      }

      toast.success('Initial inspection report updated successfully');
      navigate(`/properties/${propertyId}`);

    } catch (error) {
      console.error('Error updating initial inspection report:', error);

      // Network failure (no server response) → fall back to the offline queue.
      if (!error?.response) {
        try {
          await queueForSync();
          return;
        } catch (_) {
          /* fall through */
        }
      }

      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
        toast.error('Please fix the errors in the form');
      } else {
        toast.error('Failed to update initial inspection report');
      }
      setLoading(false);
    }
  };

  const renderFormGroup = (label, name, type = 'text', required = false, options = null, placeholder = '') => {
    const hasError = errors[name];
    const isDateField = name === 'report_date' || name === 'issued' || name === 'review_date';
    
    // Get the appropriate date input ref
    let dateInputRef = null;
    if (name === 'report_date') {
      dateInputRef = reportDateInputRef;
    } else if (name === 'issued') {
      dateInputRef = issuedDateInputRef;
    } else if (name === 'review_date') {
      dateInputRef = reviewDateInputRef;
    }
    
    return (
      <div className={`${styles.formGroup} ${hasError ? styles.hasError : ''}`}>
        <label className={required ? styles.controlLabelRequired : styles.controlLabel}>
          {label}
        </label>
        <div className={styles.colSm10}>
          {type === 'select' ? (
            <select
              name={name}
              value={formData[name]}
              onChange={handleInputChange}
              className={styles.formControl}
              required={required}
            >
              <option value="">Please select</option>
              {options && Object.entries(options).map(([value, text]) => (
                <option key={value} value={value}>{text}</option>
              ))}
            </select>
          ) : type === 'textarea' ? (
            <textarea
              name={name}
              value={formData[name]}
              onChange={handleInputChange}
              className={styles.formControl}
              required={required}
              placeholder={placeholder}
              rows={6}
            />
          ) : isDateField ? (
            // Date field with calendar picker (Laravel style)
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                name={name}
                value={formData[name] || ''}
                onChange={handleInputChange}
                className={styles.formControl}
                required={required}
                placeholder="dd/mm/yyyy"
                readOnly
                onClick={(e) => openDatePicker(name, e)}
                style={{ paddingRight: '35px', cursor: 'pointer' }}
              />
              {/* Hidden date input for native date picker */}
              <input
                ref={dateInputRef}
                type="date"
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer',
                  zIndex: 10,
                  fontSize: '16px',
                  padding: '6px 35px 6px 10px',
                  margin: 0,
                  border: 'none',
                  background: 'transparent',
                  boxSizing: 'border-box'
                }}
                value={convertToDateInputFormat(formData[name])}
                onChange={(e) => handleDateChange(name, e)}
              />
              <span
                className="glyphicon glyphicon-calendar"
                onClick={(e) => openDatePicker(name, e)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#999',
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                  zIndex: 11,
                  fontSize: '14px'
                }}
              />
            </div>
          ) : (
            <input
              type={type}
              name={name}
              value={formData[name]}
              onChange={handleInputChange}
              className={styles.formControl}
              required={required}
              placeholder={placeholder}
            />
          )}
          {hasError && <div className={styles.errorMessage}>{hasError}</div>}
        </div>
      </div>
    );
  };

  // Resolve a field's image src for the lightbox. Priority:
  //   1. Just-uploaded local data URL
  //   2. Backend-supplied presigned URL (`{field}_url` on the loaded report)
  //   3. None (slot empty)
  const resolveImageSrc = (field) => {
    const preview = photoPreviewUrls[field];
    if (preview) return preview;
    return report?.[`${field}_url`] || null;
  };

  const gallerySlides = INITIAL_INSPECTION_IMAGE_FIELDS
    .map(({ field, label }) => {
      const src = resolveImageSrc(field);
      if (!src) return null;
      return { field, src, title: label };
    })
    .filter(Boolean);

  const openGalleryAt = (field) => {
    const idx = gallerySlides.findIndex((s) => s.field === field);
    if (idx >= 0) {
      setGalleryIndex(idx);
      setGalleryOpen(true);
    }
  };

  const galleryRotations = gallerySlides.reduce((acc, slide, i) => {
    const r = photoRotations[slide.field];
    if (r) acc[i] = r;
    return acc;
  }, {});

  const handleGalleryRotate = (slideIdx) => {
    const slide = gallerySlides[slideIdx];
    if (slide) rotatePhotoField(slide.field);
  };

  const renderFileUpload = (label, name, required = false) => {
    const hasError = errors[name];
    const hasExistingImage = formData[name] && formData[name] !== null && formData[name] !== '';
    const fileName = fileNames[name];
    const imageUrl = report?.[`${name}_url`]; // Presigned URL from backend
    
    return (
      <div className={`${styles.formGroup} ${hasError ? styles.hasError : ''}`}>
        <div className={styles.colSm12}>
          {!hasExistingImage ? (
            // Show Choose File button only when no file exists
            <div className={styles.fileUploadContainer}>
              <input
                type="file"
                name={name}
                onChange={handleInputChange}
                className={styles.fileInput}
                accept="image/*"
                required={required}
                id={`${name}-upload`}
              />
              <label
                htmlFor={`${name}-upload`}
                className={styles.fileButton}
              >
                Choose File
              </label>
              <span className={styles.fileName}>
                {fileName}
              </span>
            </div>
          ) : (
            // Show file info, preview, and actions when file exists
            <div className={styles.fileUploadContainer}>
              {(photoPreviewUrls[name] || imageUrl) && (
                <div>
                  <img
                    src={photoPreviewUrls[name] || imageUrl}
                    alt={label}
                    onClick={() => openGalleryAt(name)}
                    title="Click to view larger"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '5px',
                      display: 'block',
                      marginBottom: '8px',
                      cursor: 'zoom-in',
                      transform: `rotate(${photoRotations[name] || 0}deg)`,
                      transition: 'transform 0.25s ease',
                    }}
                    onError={(e) => {
                      console.error(`Failed to load image: ${imageUrl}`);
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              <span className={styles.fileName}>
                {fileName}
              </span>
              <div className={styles.fileActions}>
                {imageUrl && (
                  <a
                    href={imageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`}
                    style={{ textDecoration: 'none' }}
                    title="View"
                  >
                    <i className="fa fa-eye"></i> View
                  </a>
                )}
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnDanger} ${styles.btnSm}`}
                  onClick={() => handleRemoveImage(name)}
                  title="Remove"
                >
                  <i className="fa fa-trash"></i> Remove
                </button>
              </div>
            </div>
          )}
          {hasError && <div className={styles.errorMessage}>{hasError}</div>}
        </div>
      </div>
    );
  };

  if (loading && !report) {
    return (
      <div className={styles.container}>
        <div className={styles.spinner}></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.panelForm}>
        <div className={styles.panelHeading}>
          <h3 className={styles.panelTitle}>Edit Initial Inspection Report</h3>
        </div>
        <div className={styles.panelBody}>
          <form onSubmit={handleSubmit} className={styles.formHorizontal}>
            <input type="hidden" name="property_id" value={propertyId} />

            {/* Report Section */}
            <div className={styles.row}>
              <div className={styles.colXs12}>
                <div className={styles.reportSectionHeader}>Report</div>
                
                <div className={styles.formGroup}>
                  <div className={styles.colXs12}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                      <button
                        type="button"
                        className={styles.btnPrimary}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          backgroundColor: '#337ab7',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                        onClick={async () => {
                          try {
                            const reportUrl = `/api/initialinspectionreports/${reportId}/download`;
                            const response = await fetch(reportUrl, {
                              headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                              }
                            });
                            if (!response.ok) {
                              throw new Error(`HTTP error! status: ${response.status}`);
                            }
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', `initial-inspection-report-${reportId}.pdf`);
                            document.body.appendChild(link);
                            link.click();
                            link.parentNode.removeChild(link);
                            window.URL.revokeObjectURL(url);
                            toast.success("Report downloaded successfully!");
                          } catch (error) {
                            console.error("Error downloading report:", error);
                            toast.error("Failed to download report. Please try again.");
                          }
                        }}
                      >
                        <i className="glyphicon glyphicon-file" style={{ color: 'white' }}></i>
                        <span>Report</span>
                        <small style={{ fontSize: '10px', opacity: 0.8 }}>
                          Updated: {new Date().toLocaleDateString('en-GB')}
                        </small>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Address Section */}
            <div className={styles.row}>
              <div className={styles.colXs12}>
                <div className={styles.reportSectionHeader}>Address</div>
                
                {renderFormGroup('Property owner', 'client', 'text', false, null, '')}
                {renderFormGroup('Address', 'address', 'text', false, null, '')}
              </div>
            </div>

            {/* General Section */}
            <div className={styles.row}>
              <div className={styles.colXs12}>
                <div className={styles.reportSectionHeader}>General</div>
              </div>
              
              <div className={styles.colXs6}>
                {renderFormGroup('Status', 'status_id', 'select', true, { 0: 'Unpublished', 1: 'Published' })}
                {renderFormGroup('Building Type', 'building_type', 'select', false, 
                  buildingTypes.reduce((acc, type) => ({ ...acc, [type]: type }), {}))}
                {renderFormGroup('How many floors', 'floors', 'number', false)}
                {renderFormGroup('How many bathrooms', 'bathrooms', 'number', false)}
                {renderFormGroup('Is there parking', 'parking', 'select', false, { 0: 'No', 1: 'Yes' })}
              </div>
              
              <div className={styles.colXs6}>
                {renderFormGroup('Council', 'what_council', 'text', false)}
                {renderFormGroup('Property for VOA', 'property_for_voa', 'select', false, { 0: 'No', 1: 'Yes' })}
                {renderFormGroup('How many toilets', 'toilets', 'number', false)}
                {renderFormGroup('How many kitchens', 'kitchens', 'number', false)}
                {renderFormGroup('Is there a garden', 'garden', 'select', false, { 0: 'No', 1: 'Yes' })}
              </div>
            </div>

            {/* Rooms Section */}
            <div className={styles.row}>
              <div className={styles.colXs12}>
                <div className={styles.reportSectionHeader}>Rooms</div>
                
                <div className={styles.formGroup}>
                  <label className={styles.controlLabelRequired}>
                    How many guardians / guardian rooms
                  </label>
                  <div className={styles.colSm2}>
                    <input
                      type="number"
                      name="rooms_number"
                      value={formData.rooms_number}
                      onChange={handleRoomsNumberChange}
                      className={styles.formControl}
                      required
                    />
                    {errors.rooms_number && <div className={styles.errorMessage}>{errors.rooms_number}</div>}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.roomsForms}>
              <div className={styles.row}>
                {Array.from({ length: roomsNumber }, (_, index) => (
                  <div key={index} className={styles.colXs3}>
                    <div className={styles.formGroup}>
                      <label className={styles.controlLabelRequired}>
                        Room {index + 1}
                      </label>
                      <div className={styles.colSm8}>
                        <select
                          value={formData.rooms[index] || ''}
                          onChange={(e) => handleRoomChange(index, e.target.value)}
                          className={styles.formControl}
                          required
                        >
                          <option value="">Please select</option>
                          {Object.entries(roomSizes).map(([value, text]) => (
                            <option key={value} value={value}>{text}</option>
                          ))}
                        </select>
                        {errors[`rooms.${index}`] && (
                          <div className={styles.errorMessage}>{errors[`rooms.${index}`]}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Maintenance Section */}
            <div className={styles.reportSectionHeader}>Maintenance</div>
            {renderFormGroup('Maintenance', 'maintenance', 'textarea', false, null, '')}

            {/* Installations Section */}
            <div className={styles.reportSectionHeader}>
              Installations <small>(How many to be installed)</small>
            </div>
            
            <div className={styles.row}>
              <div className={styles.colXs6}>
                {renderFormGroup('Shower Cubicles', 'installation_shower_cubicles', 'number', false)}
                {renderFormGroup('Shower electric units', 'installation_shower_electric_units', 'number', false)}
                {renderFormGroup('Over sink water heaters', 'installation_over_sink_water_heaters', 'number', false)}
                {renderFormGroup('Mini ovens', 'installation_mini_ovens', 'number', false)}
                {renderFormGroup('Microwaves', 'installation_microwaves', 'number', false)}
              </div>
              
              <div className={styles.colXs6}>
                {renderFormGroup('Kitchens (worktops)', 'installation_kitchens', 'number', false)}
                {renderFormGroup('Electric heaters', 'installation_electric_heaters', 'number', false)}
                {renderFormGroup('Fridges', 'installation_fridges', 'number', false)}
                {renderFormGroup('Cookers', 'installation_cookers', 'number', false)}
              </div>
            </div>

            {/* General Utilities Section */}
            <div className={styles.row}>
              <div className={styles.colXs12}>
                <div className={styles.reportSectionHeader}>General Utilities</div>
                
                {renderFormGroup('List location of meters/boards/main switches', 'locations_of_meters', 'textarea', false, null, '')}
              </div>
              
              <div className={styles.colXs3}>
                <div className={styles.mb10}><strong>Gas Meters</strong></div>
                {renderFileUpload('Gas Meters', 'image_gas_meters', false)}
              </div>
              
              <div className={styles.colXs3}>
                <div className={styles.mb10}><strong>Electric Meters</strong></div>
                {renderFileUpload('Electric Meters', 'image_electric_meters', false)}
              </div>
              
              <div className={styles.colXs3}>
                <div className={styles.mb10}><strong>Water Meters</strong></div>
                {renderFileUpload('Water Meters', 'image_water_meters', false)}
              </div>
              
              <div className={styles.colXs3}>
                <div className={styles.mb10}><strong>Fuse Boards</strong></div>
                {renderFileUpload('Fuse Boards', 'image_fuse_boards', false)}
              </div>
              
              <div className={styles.colXs12}>
                <div className={styles.row}>
                  <div className={styles.colXs4}>Utility Bills <small>Be specific to what bills</small></div>
                  
                  <div className={styles.colXs4}>
                    {renderFormGroup('Client', 'utility_bills_client', 'text', false)}
                  </div>
                  
                  <div className={styles.colXs4}>
                    {renderFormGroup('GGM', 'utility_bills_ggm', 'text', false)}
                  </div>
                </div>
                
                <div className={styles.row}>
                  <div className={styles.colXs4}>Waste Collection</div>
                  
                  <div className={styles.colXs4}>
                    {renderFormGroup('Client', 'waste_collection_client', 'text', false)}
                  </div>
                  
                  <div className={styles.colXs4}>
                    {renderFormGroup('GGM', 'waste_collection_ggm', 'text', false)}
                  </div>
                </div>
              </div>
            </div>

            {/* Accountability Section */}
            <div className={styles.row}>
              <div className={styles.colXs12}>
                <div className={styles.reportSectionHeader}>Accountability</div>
              </div>
              
              <div className={styles.colXs4}>
                {renderFormGroup('Surveyed by', 'surveyed_by', 'text', false)}
              </div>
              
              <div className={styles.colXs4}>
                {renderFormGroup('Signed', 'signed', 'text', false)}
              </div>
              
              <div className={styles.colXs4}>
                {renderFormGroup('Report Date', 'report_date', 'text', false)}
              </div>
            </div>

            {/* Footer Section */}
            <div className={styles.row}>
              <div className={styles.colXs12}>
                <div className={styles.reportSectionHeader}>Footer</div>
              </div>
              
              <div className={styles.colXs3}>
                {renderFormGroup('Form', 'form', 'text', false)}
              </div>
              
              <div className={styles.colXs3}>
                {renderFormGroup('Issued', 'issued', 'text', false)}
              </div>
              
              <div className={styles.colXs3}>
                {renderFormGroup('Review Date', 'review_date', 'text', false)}
              </div>
              
              <div className={styles.colXs3}>
                {renderFormGroup('Approved', 'approved', 'text', false)}
              </div>
            </div>

            {/* Additional Notes Section */}
            <div className={styles.reportSectionHeader}>Additional Notes</div>
            {renderFormGroup('Notes', 'notes', 'textarea', false, null, '')}

            {/* Submit Button */}
            <div className={styles.formGroup}>
              <div className={styles.colSm12}>
                <div className={styles.textRight}>
                  <button 
                    type="submit" 
                    className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                    disabled={loading}
                  >
                    {loading ? <span className={styles.spinner}></span> : 'Update'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Large image viewer */}
      <PhotoGallery
        open={galleryOpen}
        close={() => setGalleryOpen(false)}
        slides={gallerySlides}
        index={galleryIndex}
        rotations={galleryRotations}
        onRotate={handleGalleryRotate}
      />
    </div>
  );
};

export default InitialInspectionEdit; 